import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Check, Sparkles, Reply } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/Layout";
import ComposeDialog from "@/components/ComposeDialog";
import { api, errMsg, fmtDate } from "@/lib/api";

const TONE = { interested: "bg-emerald-100 text-emerald-700", meeting_request: "bg-emerald-600 text-white", question: "bg-blue-100 text-blue-700",
  not_interested: "bg-slate-200 text-slate-700", out_of_office: "bg-amber-100 text-amber-700", unsubscribe: "bg-red-100 text-red-700", other: "bg-slate-100 text-slate-600" };

export default function Inbox() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("open");
  const [cur, setCur] = useState(null);
  const [replyLead, setReplyLead] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/replies", { params: filter === "all" ? {} : { handled: filter === "done" } }).then((r) => { setList(r.data); setCur((c) => r.data.find((x) => x.id === c?.id) || r.data[0] || null); });
  useEffect(() => { load(); }, [filter]); // eslint-disable-line
  const sync = async () => {
    setBusy(true);
    try { const { data } = await api.post("/inbox/sync"); data.ok ? toast.success(`Scanned ${data.scanned}: ${data.sales_replies} sales replies, ${data.ignored} non-sales left in inbox`) : toast.error(data.error); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const handled = async (r) => { await api.patch(`/replies/${r.id}`, { handled: !r.handled }); load(); };
  const openReply = async (r) => { const { data } = await api.get(`/leads/${r.lead_id}`); setReplyLead(data); };

  return (
    <div>
      <PageHeader eyebrow="Microsoft 365" title="Sales Inbox">
        <Button onClick={sync} disabled={busy} data-testid="inbox-sync-btn"><RefreshCw size={15} className={`mr-1 ${busy ? "animate-spin" : ""}`} />Sync inbox</Button>
      </PageHeader>
      <div className="p-6 lg:p-10 space-y-4">
        <Tabs value={filter} onValueChange={setFilter}><TabsList>
          <TabsTrigger value="open" data-testid="inbox-filter-open">To handle</TabsTrigger><TabsTrigger value="done" data-testid="inbox-filter-done">Handled</TabsTrigger><TabsTrigger value="all" data-testid="inbox-filter-all">All</TabsTrigger>
        </TabsList></Tabs>
        <p className="text-xs text-slate-500">Only replies from addresses that exist as leads are pulled in. Everything else stays untouched in the Microsoft 365 inbox.</p>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 bg-white border rounded-xl divide-y overflow-hidden" data-testid="reply-list">
            {list.map((r) => (
              <button key={r.id} onClick={() => setCur(r)} data-testid={`reply-item-${r.id}`} className={`w-full text-left p-4 transition-colors ${cur?.id === r.id ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                <div className="flex justify-between gap-2"><span className="font-semibold text-sm truncate">{r.lead_name} · {r.company_name}</span><span className="text-xs text-slate-400 shrink-0">{fmtDate(r.received_at)}</span></div>
                <div className="text-sm text-slate-600 truncate">{r.subject}</div>
                <div className="flex gap-1.5 mt-1.5"><span className={`text-[10px] uppercase font-semibold rounded px-1.5 py-0.5 ${TONE[r.classification?.category] || TONE.other}`}>{r.classification?.category?.replace("_", " ")}</span>
                  {r.source === "simulated" && <span className="text-[10px] uppercase bg-amber-50 text-amber-700 rounded px-1.5 py-0.5">simulated</span>}</div>
              </button>))}
            {!list.length && <div className="p-10 text-center text-sm text-slate-500">No replies here.</div>}
          </div>
          {cur && (
            <div className="lg:col-span-7 bg-white border rounded-xl p-6 space-y-4" data-testid="reply-detail">
              <div className="flex flex-wrap justify-between gap-2">
                <div><Link to={`/leads/${cur.lead_id}`} className="font-bold text-lg hover:text-blue-600">{cur.lead_name} · {cur.company_name}</Link><div className="text-sm text-slate-500">{cur.from} · {fmtDate(cur.received_at)}</div></div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => handled(cur)} data-testid="reply-handled-btn"><Check size={15} className="mr-1" />{cur.handled ? "Reopen" : "Mark handled"}</Button>
                  <Button onClick={() => openReply(cur)} data-testid="reply-respond-btn"><Reply size={15} className="mr-1" />Respond</Button>
                </div>
              </div>
              <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-4 text-sm space-y-1" data-testid="reply-ai-classification">
                <div className="flex items-center gap-1.5 font-semibold text-violet-800"><Sparkles size={14} />AI: {cur.classification?.category?.replace("_", " ")} · {cur.classification?.sentiment}</div>
                <div className="text-slate-700">{cur.classification?.summary}</div>
                {cur.classification?.suggested_action && <div className="text-slate-500">Next: {cur.classification.suggested_action}</div>}
              </div>
              <div className="font-semibold">{cur.subject}</div>
              <div className="text-sm whitespace-pre-wrap text-slate-700 leading-relaxed">{cur.body}</div>
            </div>
          )}
        </div>
      </div>
      <ComposeDialog lead={replyLead} open={!!replyLead} onOpenChange={(o) => !o && setReplyLead(null)} advance={false} onSent={load} />
    </div>
  );
}
